import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Alert,
} from "@mui/material";
import { apiClient } from "../../services/api-client";

interface AuditLog {
  id: string;
  action: string;
  resourceType: string;
  resourceId?: string | null;
  success: boolean;
  ipAddress?: string | null;
  metadata?: Record<string, any> | null;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    role: string;
  } | null;
}

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [actionFilter, setActionFilter] = useState("");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");
  const [successFilter, setSuccessFilter] = useState<string>("all");

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, actionFilter, resourceTypeFilter, successFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append("limit", rowsPerPage.toString());
      params.append("offset", (page * rowsPerPage).toString());

      if (actionFilter) params.append("action", actionFilter);
      if (resourceTypeFilter) params.append("resourceType", resourceTypeFilter);
      if (successFilter !== "all") params.append("success", successFilter);

      const { data } = await apiClient.get<{ status: string; data: { logs: AuditLog[]; total: number } }>(
        `/audit-logs?${params.toString()}`
      );

      setLogs(data.data.logs);
      setTotalCount(data.data.total);
    } catch (err: any) {
      console.error("Failed to load audit logs:", err);
      setError(err.response?.data?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionColor = (action: string): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    if (action.includes("CREATE")) return "success";
    if (action.includes("UPDATE")) return "info";
    if (action.includes("DELETE")) return "error";
    if (action.includes("LOGIN")) return "primary";
    return "default";
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Audit Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          System activity audit trail and security monitoring
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Action"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Actions</MenuItem>
                <MenuItem value="USER_CREATE">User Create</MenuItem>
                <MenuItem value="USER_UPDATE">User Update</MenuItem>
                <MenuItem value="USER_DELETE">User Delete</MenuItem>
                <MenuItem value="CASE_CREATE">Case Create</MenuItem>
                <MenuItem value="CASE_UPDATE">Case Update</MenuItem>
                <MenuItem value="PARTICIPANT_CREATE">Participant Create</MenuItem>
                <MenuItem value="PARTICIPANT_UPDATE">Participant Update</MenuItem>
                <MenuItem value="PAYSHEET_SUBMIT">Paysheet Submit</MenuItem>
                <MenuItem value="PAYSHEET_VERIFY">Paysheet Verify</MenuItem>
                <MenuItem value="PAYSHEET_AUTHORIZE">Paysheet Authorize</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Resource Type"
                value={resourceTypeFilter}
                onChange={(e) => {
                  setResourceTypeFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Resources</MenuItem>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Case">Case</MenuItem>
                <MenuItem value="Participant">Participant</MenuItem>
                <MenuItem value="Paysheet">Paysheet</MenuItem>
                <MenuItem value="Application">Application</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Status"
                value={successFilter}
                onChange={(e) => {
                  setSuccessFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="true">Success Only</MenuItem>
                <MenuItem value="false">Failed Only</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Audit Logs Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Typography>Loading audit logs...</Typography>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Resource</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No audit logs found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            {new Date(log.createdAt).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {log.user ? (
                              <Box>
                                <Typography variant="body2">{log.user.email}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.user.role}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                System
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.action}
                              color={getActionColor(log.action)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {log.resourceType}
                            {log.resourceId && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                ID: {log.resourceId.substring(0, 8)}...
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {log.success ? (
                              <Chip label="Success" color="success" size="small" />
                            ) : (
                              <Chip label="Failed" color="error" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {log.ipAddress || "N/A"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
